/*
 * Sportsact: menu – desktop + fælles hentning af produktkort (snippets/custom-menu-desktop.liquid).
 * - Produktkort hentes først, når en kategori åbnes: /collections/<handle>?sort_by=best-selling&section_id=custom-menu-products
 *   (samme svar genbruges på desktop og mobil). Billederne indlæses derfor først, når menuen er åben.
 * - <custom-mega-menu>: desktoppanelet. Hover (mus), klik på pilen (tastatur), tryk på navnet (touch).
 *   Lukker ved Esc, klik udenfor, fokus ud og når musen forlader panelet (med forsinkelse).
 */
(() => {
  const cache = new Map();

  function fetchCards(url) {
    if (!cache.has(url)) {
      const request = fetch(url)
        .then((response) => (response.ok ? response.text() : ''))
        .then((html) => Array.from(new DOMParser().parseFromString(html, 'text/html').querySelectorAll('.custom-menu-card')))
        .catch(() => {
          cache.delete(url);
          return [];
        });

      cache.set(url, request);
    }

    return cache.get(url);
  }

  function viewAllCard(url, label) {
    const card = document.createElement('a');
    card.href = url;
    card.className = 'custom-menu-card custom-menu-card--all';

    const text = document.createElement('span');
    text.className = 'custom-menu-card__all-label';
    text.textContent = label;

    const icon = document.createElement('span');
    icon.className = 'custom-menu-card__all-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8h10m0 0L8.5 3.5M13 8l-4.5 4.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    card.append(text, icon);
    return card;
  }

  // Fylder en grid med op til "limit" produktkort + "Se alle"
  async function fillGrid(grid, { url, viewAllUrl, viewAllLabel, limit }) {
    if (!url || grid.dataset.source === url) {
      return;
    }

    grid.dataset.source = url;
    grid.setAttribute('aria-busy', 'true');
    grid.classList.add('is-loading');

    const cards = await fetchCards(url);

    if (grid.dataset.source !== url) {
      return; // en anden kategori blev åbnet imens
    }

    const fragment = document.createDocumentFragment();
    cards.slice(0, limit).forEach((card) => fragment.append(card.cloneNode(true)));
    fragment.append(viewAllCard(viewAllUrl, viewAllLabel));

    grid.replaceChildren(fragment);
    grid.removeAttribute('aria-busy');
    grid.classList.remove('is-loading');
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * DESKTOP
   * ------------------------------------------------------------------------------------------------------------------ */
  class CustomMegaMenu extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.items = Array.from(this.querySelectorAll('.custom-menu__item[data-expandable]'));
      this.openItem = null;
      this.closeDelay = 300;
      this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

      // Hent alle kategoriers HTML (ikke billeder), så snart musen nærmer sig menuen
      this.addEventListener('pointerenter', () => this.items.forEach((item) => this.prefetch(item)), { once: true });

      this.items.forEach((item) => {
        const toggle = item.querySelector('.custom-menu__toggle');
        const link = item.querySelector('a.custom-menu__link');

        item.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(this.closeTimer);
          clearTimeout(this.openTimer);
          this.openTimer = setTimeout(() => this.open(item), this.openItem ? 0 : 80);
        });

        item.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(this.openTimer);
          this.closeTimer = setTimeout(() => this.close(item), this.closeDelay);
        });

        toggle.addEventListener('click', () => (this.openItem === item ? this.close(item) : this.open(item)));

        // Touch: første tryk på navnet åbner panelet (panelet har "Se alle" til kollektionen)
        link?.addEventListener('click', (event) => {
          if (!this.finePointer.matches && this.openItem !== item) {
            event.preventDefault();
            this.open(item);
          }
        });

        link?.addEventListener('focus', () => this.prefetch(item));

        item.addEventListener('focusout', (event) => {
          if (this.openItem === item && event.relatedTarget && !item.contains(event.relatedTarget)) {
            this.close(item);
          }
        });
      });

      this._onKeydown = (event) => {
        if (event.key !== 'Escape' || !this.openItem) return;
        const item = this.openItem;
        const hadFocus = item.contains(document.activeElement);
        this.close(item);
        if (hadFocus) item.querySelector('.custom-menu__toggle').focus();
      };

      this._onDocumentClick = (event) => {
        if (this.openItem && !this.openItem.contains(event.target)) {
          this.close(this.openItem);
        }
      };

      document.addEventListener('keydown', this._onKeydown);
      document.addEventListener('click', this._onDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKeydown);
      document.removeEventListener('click', this._onDocumentClick);
      this._ready = false;
    }

    panelOf(item) {
      return item.querySelector('.custom-menu__panel');
    }

    prefetch(item) {
      const url = this.panelOf(item).dataset.url;
      if (url) fetchCards(url);
    }

    open(item) {
      clearTimeout(this.closeTimer);
      if (this.openItem === item) return;
      if (this.openItem) this.close(this.openItem, true);

      const panel = this.panelOf(item);
      const grid = panel.querySelector('[data-products]');

      // Underkategori-kort: kopieres fra mobilmenuens <template> første gang (runde 15)
      const cardsGrid = panel.querySelector('[data-cards-from]');

      if (cardsGrid && !cardsGrid.hasChildNodes()) {
        const template = document.querySelector(`template[data-menu-cards="${cardsGrid.dataset.cardsFrom}"]`);
        if (template) cardsGrid.append(template.content.cloneNode(true));
      }

      if (grid) {
        fillGrid(grid, {
          url: panel.dataset.url,
          viewAllUrl: panel.dataset.viewAllUrl,
          viewAllLabel: this.dataset.viewAll,
          limit: parseInt(this.dataset.limit, 10) || 6,
        });
      }

      panel.hidden = false;
      item.classList.add('is-open');
      item.querySelector('.custom-menu__toggle').setAttribute('aria-expanded', 'true');
      this.openItem = item;
    }

    close(item, immediate = false) {
      if (!item || this.openItem !== item) return;

      const panel = this.panelOf(item);
      item.classList.remove('is-open');
      item.querySelector('.custom-menu__toggle').setAttribute('aria-expanded', 'false');
      this.openItem = null;

      if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        panel.hidden = true;
        return;
      }

      panel.classList.add('is-closing');
      setTimeout(() => {
        panel.classList.remove('is-closing');
        if (this.openItem !== item) panel.hidden = true;
      }, 150);
    }
  }

  if (!customElements.get('custom-mega-menu')) {
    customElements.define('custom-mega-menu', CustomMegaMenu);
  }

  // Deles med mobilmenuen (assets/custom-menu-drawer.js)
  window.SportsactMenu = { fetchCards, fillGrid };
})();
